import type { AxiosResponse } from 'axios';
import Repository from './repository';

const resource = '/billing/top-up';
const subscriptionResource = '/billing/subscription';
const invoicesResource = '/billing/invoices';

type CurrencyCode = 'EUR' | 'USD' | 'GBP';
type SubscriptionInterval = 'monthly' | 'annual';

export interface CreateTopUpCheckoutPayload {
	amount: string;
	currency: CurrencyCode;
	returnPath: string;
}

export interface CreateSubscriptionCheckoutPayload {
	packageCode: string;
	interval: SubscriptionInterval;
	currency: CurrencyCode;
	bespokeAmount?: string;
	returnPath: string;
}

export default {
	createTopUpCheckout(
		payload: CreateTopUpCheckoutPayload
	): Promise<AxiosResponse> {
		return Repository.postWithAuth(`${resource}/checkout`, payload);
	},
	getTopUpStatus(checkoutSessionId: string): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/status/${checkoutSessionId}`,
			{},
			{},
			{ noCache: true }
		);
	},
	// Top-ups that have been paid for but whose credit has not landed on the
	// balance yet. Lets a page show the processing state without having been
	// opened through the checkout return URL.
	getPendingTopUps(): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/pending`,
			{},
			{},
			{ noCache: true }
		);
	},
	getTopUpLimits(currency: CurrencyCode): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${resource}/limits`,
			{},
			{},
			{ params: { currency }, noCache: true }
		);
	},
	createSubscriptionCheckout(
		payload: CreateSubscriptionCheckoutPayload
	): Promise<AxiosResponse> {
		return Repository.postWithAuth(`${subscriptionResource}/checkout`, payload);
	},
	getSubscriptionStatus(subscriptionId: string): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${subscriptionResource}/status/${subscriptionId}`,
			{},
			{},
			{ noCache: true }
		);
	},
	createSubscriptionPortalSession(payload: {
		returnPath: string;
	}): Promise<AxiosResponse> {
		return Repository.postWithAuth(`${subscriptionResource}/manage`, payload);
	},
	getActiveSubscription(): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${subscriptionResource}/active`,
			{},
			{},
			{ noCache: true }
		);
	},
	listInvoices(params: {
		page: number;
		limit: number;
	}): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			invoicesResource,
			{},
			{},
			{ params, noCache: true }
		);
	},
	getInvoiceDownloadURL(id: string): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`${invoicesResource}/${id}/download`,
			{},
			{},
			{ noCache: true }
		);
	},
	downloadInvoicePDF(id: string): Promise<AxiosResponse<Blob>> {
		return Repository.getWithAuth(
			`${invoicesResource}/${id}/pdf`,
			{},
			undefined,
			{ noCache: true, responseType: 'blob' }
		);
	},
	lookupInvoicesByReferences(references: string[]): Promise<AxiosResponse> {
		return Repository.postWithAuth(`${invoicesResource}/lookup-by-references`, {
			references,
		});
	},
	lookupMerchantInvoicesByReferences(
		merchantId: string,
		references: string[]
	): Promise<AxiosResponse> {
		return Repository.postWithAuth(
			`/billing/merchants/${merchantId}/invoices/lookup-by-references`,
			{ references }
		);
	},
	createProForma(payload: {
		merchantId: string;
		amount: string;
		currency: CurrencyCode;
		send: boolean;
	}): Promise<AxiosResponse> {
		return Repository.postWithAuth('/billing/pro-formas', payload);
	},
	sendProForma(merchantId: string, invoiceId: string): Promise<AxiosResponse> {
		return Repository.postWithAuth(
			`/billing/merchants/${merchantId}/pro-formas/${invoiceId}/send`,
			{}
		);
	},
	listMerchantInvoices(
		merchantId: string,
		params: {
			page?: number;
			limit?: number;
			source?: string;
			status?: string;
		} = {}
	): Promise<AxiosResponse> {
		return Repository.getWithAuth(
			`/billing/merchants/${merchantId}/invoices`,
			{},
			{},
			{ params, noCache: true }
		);
	},
	markInvoicePaid(invoiceId: string): Promise<AxiosResponse> {
		return Repository.postWithAuth(
			`/billing/invoices/${invoiceId}/mark-paid`,
			{}
		);
	},
	deleteProForma(
		merchantId: string,
		invoiceId: string
	): Promise<AxiosResponse> {
		return Repository.deleteWithAuth(
			`/billing/merchants/${merchantId}/pro-formas/${invoiceId}`
		);
	},
};
